import React, { useState, useMemo, useEffect, useRef } from 'react';
import { API_BASE_URL, AUTHORIZED_ROLES } from '../constants/property';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Grid from '../components/Grid';
import Pagination from '../components/Pagination';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { plantNames } from '../constants/property';
import { Snackbar, Alert } from '@mui/material';
import { formatIndianGstNumber, isValidIndianGstNumber } from '../utils/gst';
import {
  parseRecordDateTime,
  compareRecordsNewestFirst,
  defaultSortDirectionForColumn,
} from '../utils/tableSort';

const ModalSection = ({ children }) => (
  <section className="mb-6 last:mb-0">
    {children}
  </section>
);

const StockOut = () => {
  const location = useLocation();
  const { hasAnyRole } = useAuth();
  const isAdministrator = hasAnyRole(AUTHORIZED_ROLES);
  
  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /** Same inclusive formula as Vendors / StockIn: amount × (1 + GST%/100). */
  const parseStockOutGstPercent = (raw) => {
    const s = String(raw ?? '').trim().replace(/%$/, '').replace(/,/g, '');
    if (!s) return null;
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  };

  const computeStockOutAmountWithGST = (amountStr, gstPercentageStr) => {
    const a = parseFloat(String(amountStr ?? '').replace(/[₹,]/g, ''));
    if (!Number.isFinite(a) || a <= 0) return '';
    const pct = parseStockOutGstPercent(gstPercentageStr);
    if (pct == null) return '';
    return (a * (1 + pct / 100)).toFixed(2);
  };

  const getStockOutRowWithGST = (row) => {
    if (row.withGST !== undefined && row.withGST !== '') return row.withGST;
    return computeStockOutAmountWithGST(row.totalamount, row.gstPercentage);
  };

  const applyStockOutGstAndBalance = (state) => ({
    ...state,
    withGST: computeStockOutAmountWithGST(state.totalamount, state.gstPercentage),
  });

  // Helper function to convert "Oct 01, 2025" format to YYYY-MM-DD for input
  const formatDateForInput = (dateString) => {
    if (!dateString) return getTodayDate();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const parts = dateString.split(' ');
    if (parts.length === 3) {
      const month = months.indexOf(parts[0]);
      const day = parseInt(parts[1].replace(',', ''), 10);
      const year = parseInt(parts[2], 10);
      if (month !== -1 && !isNaN(day) && !isNaN(year)) {
        return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
    return getTodayDate();
  };
  
  const [stockOutMaterialsFromApi, setStockOutMaterialsFromApi] = useState([]);
  const [fromPlantOptionsList, setFromPlantOptionsList] = useState(() => {
    const src = plantNames?.length ? plantNames : [{ name: 'Guindy', value: 'Guindy', label: 'Guindy' }];
    return src.map((p) => ({ value: p.value, label: p.label || p.value }));
  });
  const defaultPlant = fromPlantOptionsList[0]?.value ?? 'Guindy';
  const [maintenanceSiteCodeOptions, setMaintenanceSiteCodeOptions] = useState(() => [
    { value: 'st1998', label: 'ST1998' },
    { value: 'st2765', label: 'ST2765' },
    { value: 'st3865', label: 'ST3865' },
    { value: 'st4765', label: 'ST4765' },
    { value: 'st19098', label: 'ST19098' },
    { value: 'st2003', label: 'ST2003' },
  ]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // Report filters
  const [datePreset, setDatePreset] = useState(''); // today|yesterday|last7|last30|thisMonth|lastMonth|custom
  const [dateDisplay, setDateDisplay] = useState('');
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedMaterialType, setSelectedMaterialType] = useState('all');
  const [selectedVehicleNo, setSelectedVehicleNo] = useState('all');
  const [selectedVehicleType, setSelectedVehicleType] = useState('all');
  const [selectedFromPlant, setSelectedFromPlant] = useState('all');
  const [selectedToSites, setSelectedToSites] = useState('all');
  const [selectedDCNumber, setSelectedDCNumber] = useState('all');
  const [materialTypeInputValue, setMaterialTypeInputValue] = useState('');
  const [showMaterialTypeDropdown, setShowMaterialTypeDropdown] = useState(false);
  const [vehicleNoInputValue, setVehicleNoInputValue] = useState('');
  const [showVehicleNoDropdown, setShowVehicleNoDropdown] = useState(false);
  const [vehicleTypeInputValue, setVehicleTypeInputValue] = useState('');
  const [showVehicleTypeDropdown, setShowVehicleTypeDropdown] = useState(false);
  const [fromPlantInputValue, setFromPlantInputValue] = useState('');
  const [showFromPlantDropdown, setShowFromPlantDropdown] = useState(false);
  const [toSitesInputValue, setToSitesInputValue] = useState('');
  const [showToSitesDropdown, setShowToSitesDropdown] = useState(false);
  const [dcNumberInputValue, setDCNumberInputValue] = useState('');
  const [showDCNumberDropdown, setShowDCNumberDropdown] = useState(false);
  const [supervisorsFromApi, setSupervisorsFromApi] = useState([]);
  const [formData, setFormData] = useState({
    date: getTodayDate(),
    dcNumber: '',
    transporter: '',
    supervisor: '',
    materialtype: '',
    grade: '',
    rate: '',
    grosswt: '',
    tarewt: '',
    netwt: '',
    qty: '',
    vehicleno: '',
    vehicletype: '',
    fromplant: defaultPlant,
    tosites: '',
    sitecode: '',
    totalamount: '',
    invoiceNumber: '',
    gstNumber: '',
    gstPercentage: '',
    advance: '',
    balanceAmount: '',
    stockOut:''
  });
  const [showFilters, setShowFilters] = useState(true);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const defaultVisibleColumns = {
    date: true,
    dcNumber: true,
    materialtype: true,
    grade: true,
    grosswt: true,
    tarewt: true,
    netwt: true,
    qty: true,
    vehicleno: true,
    vehicletype: true,
    transporter: true,
    supervisor: true,
    fromplant: true,
    tosites: true,
    invoiceNumber: true,
    gstNumber: true,
    gstPercentage: true,
    withGST: true,
    totalamount: true,
    balanceAmount: false,
    actions: true
  };
  const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns);
  const [isLoading, setIsLoading] = useState(true);

  // Check if modal should be opened from navigation
  useEffect(() => {
    if (location.state?.openModal) {
      setIsModalOpen(true);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Fetch supervisors (role = Supervisor) from employee API
  useEffect(() => {
    const fetchSupervisors = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/employee`, {
          method: 'GET',
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        const options = (Array.isArray(data) ? data : [])
          .filter(emp => emp.role === 'Supervisor')
          .map(emp => {
            const label =
              emp.userName ||
              emp.employeeName ||
              `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
            return label ? { value: label, label } : null;
          })
          .filter(Boolean);

        setSupervisorsFromApi(options);
      } catch (err) {
        console.error('Error fetching supervisors from employee API:', err);
      }
    };

    fetchSupervisors();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/maintence/Plant`, { method: 'GET', mode: 'cors' });
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        const filtered = list.filter((r) => !r.type || String(r.type).toLowerCase() === 'plant');
        const opts = filtered
          .map((r) => ({
            value: String(r.value ?? r.code ?? '').trim(),
            label: String(r.value ?? r.code ?? '').trim(),
          }))
          .filter((o) => o.value);
        if (!cancelled && opts.length) setFromPlantOptionsList(opts);
      } catch {
        /* keep seeded plant list */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/maintence/Site`, { method: 'GET', mode: 'cors' });
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        const filtered = list.filter((r) => !r.type || String(r.type).toLowerCase() === 'site');
        const seen = new Set();
        const opts = filtered
          .map((r) => {
            const label = String(r.value ?? r.code ?? '').trim();
            if (!label) return null;
            const value = label.replace(/\s+/g, '').toLowerCase();
            if (seen.has(value)) return null;
            seen.add(value);
            return { value, label };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        const withoutOthers = opts.filter((o) => o.value !== 'others');
        if (!cancelled) setMaintenanceSiteCodeOptions(withoutOthers);
      } catch {
        /* keep seeded site list */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch stock-out material types from the Material Type maintenance API
  useEffect(() => {
    const fetchMaterialTypes = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/material/bytype?type=stockOutPlant&category=%22%22`, { method: 'GET', mode: 'cors' });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : (Array.isArray(data?.materials) ? data.materials : []);
          if (list.length) setStockOutMaterialsFromApi(list);
        }
      } catch (err) {
        console.error('Error fetching material types for StockOut:', err);
      }
    };
    fetchMaterialTypes();
  }, []);

  // Helper function to transform API data to component structure
  const transformApiData = (apiData) => {
    const records = [];
    let recordId = 1;
    const list = Array.isArray(apiData)
      ? apiData
      : Array.isArray(apiData?.value)
        ? apiData.value
        : Array.isArray(apiData?.data)
          ? apiData.data
          : [];

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getMonth()]} ${String(date.getDate()).padStart(2, '0')}, ${date.getFullYear()}`;
    };

    const formatNetWeight3 = (val) => {
      if (val == null || val === '') return '-';
      const n = parseFloat(val);
      if (Number.isNaN(n)) return String(val);
      if (n === 0) return '';
      return n.toFixed(3);
    };

    let apiIndex = 0;

    list.forEach((item) => {
      const nw = item.netWeight ?? item.net_weight;
      const record = {
        id: recordId++,
        apiIndex: apiIndex++,
        rawDate: item.date || '',
        stockOutId:
          item.stockOutId != null && item.stockOutId !== ''
            ? String(item.stockOutId)
            : item.id != null && item.id !== ''
              ? String(item.id)
              : '',
        stockOut: item.stockOut != null && item.stockOut !== '' ? String(item.stockOut) : '',
        date: formatDate(item.date),
        dcNumber: item.dcNumber || '',
        materialtype: item.materialType || '',
        grade: item.grade || '',
        grosswt: item.gross ? item.gross.toString() : '',
        tarewt: item.tarWeight ? item.tarWeight.toString() : (item.tareWeight ? item.tareWeight.toString() : '-'),
        netwt: nw != null && nw !== '' ? formatNetWeight3(nw) : '',
        qty: item.quanity ? item.quanity.toString() : (item.quantity ? item.quantity.toString() : '-'),
        vehicleno: item.vehicleNumber || '',
        vehicletype: item.vehicleType || '',
        transporter: item.transporter || '',
        supervisor: item.supervisor || '',
        fromplant: item.fromPlants || '-',
        tosites: item.toSites || '-',
        invoiceNumber:
          item.invoiceNumber != null && item.invoiceNumber !== ''
            ? String(item.invoiceNumber)
            : item.invoiceNo != null && item.invoiceNo !== ''
              ? String(item.invoiceNo)
              : '',
        gstNumber:
          item.gstNumber != null && item.gstNumber !== ''
            ? formatIndianGstNumber(String(item.gstNumber))
            : item.gstNo != null && item.gstNo !== ''
              ? formatIndianGstNumber(String(item.gstNo))
              : '',
        gstPercentage:
          item.gstPercentage != null && item.gstPercentage !== ''
            ? String(item.gstPercentage)
            : '',
        advance: item.advanceToDriver != null && item.advanceToDriver !== '' ? String(item.advanceToDriver) : '',
        totalamount: item.totalAmount != null && item.totalAmount !== ''
          ? String(item.totalAmount)
          : (item.advanceToDriver != null ? String(item.advanceToDriver) : '0'),
        withGST: computeStockOutAmountWithGST(
          item.totalAmount != null ? item.totalAmount : item.advanceToDriver,
          item.gstPercentage
        ),
        balanceAmount: item.balanceAmount != null && item.balanceAmount !== '' ? String(item.balanceAmount) : '0',
      };
      records.push(record);
    });

    records.sort(compareRecordsNewestFirst);
    return records;
  };

  // Helper function to fetch data from API
  const fetchWithCorsFallback = async (url) => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }
  };

  // Fetch data from API
  const fetchStockOutData = async () => {
    setIsLoading(true);
    try {
      const apiUrl = `${API_BASE_URL}/api/stockIn/stockout`;
      const apiData = await fetchWithCorsFallback(apiUrl);
      const transformedData = transformApiData(apiData);
      setStockOutData(transformedData);
    } catch (error) {
      console.error('Error fetching stock out data:', error);
      // Keep existing data if fetch fails
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStockOutData();
  }, []);

  const [stockOutData, setStockOutData] = useState([
    { 
      id: 1,
      date: "Oct 01, 2025",
      dcNumber: "DC001234",
      transporter: "ABC Transport",
      materialtype: "RMC",
      grade: "M10",
      grosswt: "",
      tarewt: "-",
      netwt: "",
      qty: "140",
      vehicleno: "TN13 2345",
      vehicletype: "Own",
      fromplant: "Plant 1",
      tosites: "Sites 1",
      totalamount: "10000",
      advance: "2000"
    },
    {
    
      id: 2,
      date: "Oct 02, 2025",
      dcNumber: "DC001235",
      transporter: "XYZ Logistics",
      materialtype: "BT",
      grade: "BC",
      grosswt: "200",
      tarewt: "50",
      netwt: "50",
      qty: "410",
      vehicleno: "TN13 5875",
      vehicletype: "Rent",
      fromplant: "Plant 2",
      tosites: "KKN",
      totalamount: "15000",
      advance: "2500"
    },
    {
      id: 3,
      date: "Oct 03, 2025",
      dcNumber: "DC001236",
      transporter: "Fast Track Transport",
      materialtype: "BT",
      grade: "BC",
      grosswt: "550",
      tarewt: "20",
      netwt: "30",
      qty: "520",
      vehicleno: "TN24 2574",
      vehicletype: "Own",
      fromplant: "Plant 3",
      tosites: "Sites 2",
      totalamount: "10000",
      advance: "2000"
    },
    {
      id: 4,
      date: "Oct 04, 2025",
      dcNumber: "DC001237",
      transporter: "Reliable Cargo",
      materialtype: "RMC",
      grade: "M20",
      grosswt: "",
      tarewt: "-",
      netwt: "",
      qty: "600",
      vehicleno: "TN32 2875",
      vehicletype: "Rent",
      fromplant: "Plant 3",
      tosites: "Sites 3",
      totalamount: "35000",
      advance: "3000"
    },
    {
      id: 5,
      date: "Oct 05, 2025",
      dcNumber: "DC001238",
      transporter: "Speed Logistics",
      materialtype: "BM",
      grade: "BM",
      grosswt: "820",
      tarewt: "10",
      netwt: "10",
      qty: "820",
      vehicleno: "TN41 5345",
      vehicletype: "Rent",
      fromplant: "Plant 2",
      tosites: "MNK",
      totalamount: "20000",
      advance: "5000"
    },
    {
      id: 6,
      date: "Oct 06, 2025",
      dcNumber: "DC001239",
      transporter: "Quick Delivery",
      materialtype: "BM",
      grade: "BM",
      grosswt: "300",
      tarewt: "50",
      netwt: "50",
      qty: "582",
      vehicleno: "TN10 2587",
      vehicletype: "Own",
      fromplant: "Plant 1",
      tosites: "Sites 4",
      totalamount: "40000",
      advance: "6000"
    },
    {
      id: 7,
      date: "Oct 07, 2025",
      dcNumber: "DC001240",
      transporter: "Heavy Duty Transport",
      materialtype: "BT",
      grade: "BM",
      grosswt: "200",
      tarewt: "20",
      netwt: "80",
      qty: "985",
      vehicleno: "TN11 5748",
      vehicletype: "Own",
      fromplant: "Plant 2",
      tosites: "Sites 1",
      totalamount: "25000",
      advance: "7000"
    },
    {
      id: 8,
      date: "Oct 08, 2025",
      dcNumber: "DC001241",
      transporter: "Bulk Transport Co",
      materialtype: "RMC",
      grade: "M20",
      grosswt: "",
      tarewt: "-",
      netwt: "",
      qty: "456",
      vehicleno: "TN25 8345",
      vehicletype: "Own",
      fromplant: "Plant 3",
      tosites: "MMK",
      totalamount: "50000",
      advance: "8000"
    },
    {
      id: 9,
      date: "Oct 09, 2025",
      dcNumber: "DC001242",
      transporter: "Industrial Logistics",
      materialtype: "BM",
      grade: "BM",
      grosswt: "680",
      tarewt: "50",
      netwt: "30",
      qty: "200",
      vehicleno: "TN43 6345",
      vehicletype: "Own",
      fromplant: "Plant 3",
      tosites: "KKN",
      totalamount: "40000",
      advance: "9000"
    },
    {
      id: 10,
      date: "Oct 10, 2025",
      dcNumber: "DC001243",
      transporter: "Eco Transport",
      materialtype: "BT",
      grade: "BC",
      grosswt: "400",
      tarewt: "25",
      netwt: "75",
      qty: "874",
      vehicleno: "TN43 534",
      vehicletype: "Rent",
      fromplant: "Plant 2",
      tosites: "MMK",
      totalamount: "20000",
      advance: "2000"
    },
  
  
   
  ]);

  // Filter options
  const materialTypeOptions = useMemo(() => {
    if (stockOutMaterialsFromApi.length > 0) {
      return stockOutMaterialsFromApi
        .map((m) => {
          const name = String(m.materialType ?? m.materialtype ?? '').trim();
          return name ? { value: name, label: name } : null;
        })
        .filter(Boolean);
    }
    // Fallback to hardcoded list when API returns nothing
    return [
      { value: 'RMC', label: 'RMC' },
      { value: 'BT', label: 'BT' },
      { value: 'BM', label: 'BM' },
    ];
  }, [stockOutMaterialsFromApi]);

  const vehicleTypeOptions = [
    { value: 'Own', label: 'Own' },
    { value: 'Rent', label: 'Rent' },
  ];

  const fromPlantOptions = fromPlantOptionsList;

  const siteCodes = maintenanceSiteCodeOptions;

  // Build filterable option lists
  const materialTypeFilterOptions = useMemo(() => {
    return [{ value: 'all', label: 'All' }, ...materialTypeOptions];
  }, [materialTypeOptions]);

  const vehicleNoOptions = useMemo(() => {
    const uniqueVehicles = [...new Set(stockOutData.map(item => item.vehicleno))];
    return [{ value: 'all', label: 'All' }, ...uniqueVehicles.map(v => ({ value: v, label: v }))];
  }, [stockOutData]);

  const vehicleTypeFilterOptions = useMemo(() => {
    return [{ value: 'all', label: 'All' }, ...vehicleTypeOptions];
  }, []);

  const fromPlantFilterOptions = useMemo(() => {
    return [{ value: 'all', label: 'All' }, ...fromPlantOptions];
  }, [fromPlantOptions]);

  const toSitesFilterOptions = useMemo(() => {
    const fromCodes = siteCodes.map((o) => o.label);
    const fromData = [...new Set(stockOutData.map((item) => item.tosites).filter(Boolean))];
    const merged = [...new Set([...fromCodes, ...fromData])].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
    return [{ value: 'all', label: 'All' }, ...merged.map((s) => ({ value: s, label: s }))];
  }, [stockOutData, siteCodes]);

  const dcNumberOptions = useMemo(() => {
    const uniqueDCNumbers = [...new Set(stockOutData.map(item => item.dcNumber))];
    return [{ value: 'all', label: 'All' }, ...uniqueDCNumbers.map(dc => ({ value: dc, label: dc }))];
  }, [stockOutData]);

  const supervisorOptions = useMemo(() => {
    if (supervisorsFromApi && supervisorsFromApi.length > 0) {
      return supervisorsFromApi;
    }
    const supervisors = [...new Set(stockOutData.map(item => item.supervisor).filter(Boolean))].sort();
    return supervisors.map(s => ({ value: s, label: s }));
  }, [stockOutData, supervisorsFromApi]);

  // Date helpers (same as userDetails.js)
  const isDateInRange = (date, startDate, endDate) => {
    if (!startDate || !endDate || !date) return false;
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const s = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const e = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    return d >= s && d <= e;
  };

  const parseDateValue = (dateValue) => {
    if (!dateValue) return new Date(0);
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const [year, month, day] = dateValue.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    if (typeof dateValue === 'string') {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const parts = dateValue.split(' ');
      if (parts.length === 3) {
        const month = months.indexOf(parts[0]);
        const day = parseInt(parts[1].replace(',', ''), 10);
        const year = parseInt(parts[2], 10);
        if (month !== -1 && !isNaN(day) && !isNaN(year)) {
          return new Date(year, month, day);
        }
      }
    }
    const parsed = new Date(dateValue);
    return Number.isNaN(parsed.getTime())
      ? new Date(0)
      : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  };

  // Check if any filters are applied
  const hasActiveFilters = useMemo(() => {
    const vehicleNoActive = String(vehicleNoInputValue ?? '').trim() !== '';
    const dcActive = String(dcNumberInputValue ?? '').trim() !== '';
    return datePreset !== '' || dateDisplay !== '' || selectedMaterialType !== 'all' || vehicleNoActive || selectedVehicleType !== 'all' || selectedFromPlant !== 'all' || selectedToSites !== 'all' || dcActive;
  }, [datePreset, dateDisplay, selectedMaterialType, vehicleNoInputValue, selectedVehicleType, selectedFromPlant, selectedToSites, dcNumberInputValue]);

  const filteredData = useMemo(() => {
    let startDate = null;
    let endDate = null;
    const today = new Date();

    if (datePreset && datePreset !== 'custom') {
      switch (datePreset) {
        case 'today': {
          const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = d;
          endDate = d;
          break;
        }
        case 'yesterday': {
          const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
          startDate = d;
          endDate = d;
          break;
        }
        case 'last7':
          endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
          break;
        case 'last30':
          endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29);
          break;
        case 'thisMonth':
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          break;
        case 'lastMonth':
          startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          endDate = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        default:
          break;
      }
    } else if (datePreset === 'custom' && customStartDate && customEndDate) {
      startDate = customStartDate;
      endDate = customEndDate;
    }

    let filtered = stockOutData.filter(item => {
      const materialOk = selectedMaterialType === 'all' || (item.materialtype || '').toString().toLowerCase() === selectedMaterialType.toString().toLowerCase();
      const vehicleNoSearch = String(vehicleNoInputValue ?? '').trim().toLowerCase();
      const vehicleNoOk = !vehicleNoSearch || (item.vehicleno || '').toString().toLowerCase().includes(vehicleNoSearch);
      const vehicleTypeOk = selectedVehicleType === 'all' || (item.vehicletype || '').toString().toLowerCase() === selectedVehicleType.toString().toLowerCase();
      const fromPlantOk = selectedFromPlant === 'all' || (item.fromplant || '').toString().toLowerCase() === selectedFromPlant.toString().toLowerCase();
      const toSitesOk = selectedToSites === 'all' || (item.tosites || '').toString().toLowerCase() === selectedToSites.toString().toLowerCase();
      const dcNumberSearch = String(dcNumberInputValue ?? '').trim().toLowerCase();
      const dcNumberOk = !dcNumberSearch || (item.dcNumber || '').toString().toLowerCase().includes(dcNumberSearch);

      let dateOk = true;
      if (startDate && endDate) {
        const itemDate = parseDateValue(item.date);
        dateOk = isDateInRange(itemDate, startDate, endDate);
      }

      return materialOk && vehicleNoOk && vehicleTypeOk && fromPlantOk && toSitesOk && dcNumberOk && dateOk;
    });

    // Apply sorting
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortColumn];
        let bValue = b[sortColumn];

        // Handle different data types
        if (sortColumn === 'date') {
          aValue = parseRecordDateTime(a.rawDate || aValue);
          bValue = parseRecordDateTime(b.rawDate || bValue);
        } else if (sortColumn === 'withGST') {
          const num = (r) => parseFloat(String(getStockOutRowWithGST(r) || '').replace(/[₹,]/g, '')) || 0;
          aValue = num(a);
          bValue = num(b);
        } else if (sortColumn === 'totalamount') {
          aValue = parseFloat(String(a.advance ?? a.totalamount ?? '').replace(/[₹,]/g, '')) || 0;
          bValue = parseFloat(String(b.advance ?? b.totalamount ?? '').replace(/[₹,]/g, '')) || 0;
        } else if (sortColumn === 'balanceAmount' || sortColumn === 'gstPercentage' || sortColumn === 'qty' || sortColumn === 'grosswt' || sortColumn === 'tarewt' || sortColumn === 'netwt') {
          // Handle numeric values
          aValue = parseFloat(String(aValue || '').replace(/[₹,%]/g, '')) || 0;
          bValue = parseFloat(String(bValue || '').replace(/[₹,%]/g, '')) || 0;
        } else {
          // Handle string values
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return compareRecordsNewestFirst(a, b);
      });
    }

    return filtered;
  }, [stockOutData, selectedMaterialType, vehicleNoInputValue, selectedVehicleType, selectedFromPlant, selectedToSites, dcNumberInputValue, datePreset, customStartDate, customEndDate, sortColumn, sortDirection]);

  // Pagination logic (based on filtered results)
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection(defaultSortDirectionForColumn(column));
    }
    setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return (
        <svg className="w-4 h-4 text-white opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    if (sortDirection === 'asc') {
      return (
        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => {
      const visibleCount = Object.values(prev).filter(v => v).length;
      if (prev[columnKey] && visibleCount === 1) {
        return prev;
      }
      return {
        ...prev,
        [columnKey]: !prev[columnKey]
      };
    });
  };
  const handleResetColumns = () => {
    setVisibleColumns(defaultVisibleColumns);
  };

  const columnDefinitions = [
    { key: 'date', label: 'Date' },
    { key: 'dcNumber', label: 'DC Number' },
    { key: 'materialtype', label: 'Material Type' },
    { key: 'grade', label: 'Grade' },
    { key: 'grosswt', label: 'Gross Wt' },
    { key: 'tarewt', label: 'Tare' },
    { key: 'netwt', label: 'Net Wt' },
    { key: 'qty', label: 'Quantity' },
    { key: 'vehicleno', label: 'Vehicle No' },
    { key: 'vehicletype', label: 'Vehicle Type' },
    { key: 'transporter', label: 'Transporter' },
    { key: 'supervisor', label: 'Supervisor' },
    { key: 'fromplant', label: 'From Plant' },
    { key: 'tosites', label: 'To Sites' },
    { key: 'invoiceNumber', label: 'Invoice Number' },
    { key: 'gstNumber', label: 'GST Number' },
    { key: 'gstPercentage', label: 'GST(%)' },
    { key: 'withGST', label: 'With GST' },
    { key: 'totalamount', label: 'Advance to Driver' },
    { key: 'actions', label: 'Actions' }
  ];

  const getVisibleColumnCount = () => {
    return Object.values(visibleColumns).filter(v => v).length;
  };

  // Date preset display and handling
  useEffect(() => {
    if (!datePreset) {
      setDateDisplay('');
      return;
    }
    
    const now = new Date();
    const toDisplay = (d) => d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    const clone = (d) => new Date(d.getTime());
    let start = clone(now);
    let end = clone(now);
    
    switch (datePreset) {
      case 'today':
        break;
      case 'yesterday':
        start.setDate(start.getDate() - 1); 
        end.setDate(end.getDate() - 1); 
        break;
      case 'last7':
        start.setDate(start.getDate() - 6); 
        break;
      case 'last30':
        start.setDate(start.getDate() - 29); 
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1); 
        break;
      case 'lastMonth': {
        const firstLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        start = firstLastMonth; 
        end = lastLastMonth; 
        break;
      }
      case 'custom':
        if (!dateDisplay) {
          setDateDisplay(`${toDisplay(start)} - ${toDisplay(end)}`);
        }
        return;
      default:
        break;
    }
    setDateDisplay(`${toDisplay(start)} - ${toDisplay(end)}`);
  }, [datePreset]);

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    setShowDateDropdown(false);
    if (preset === 'custom') {
      setShowCustomCalendar(true);
    } else {
      setShowCustomCalendar(false);
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  };

  const isSameDate = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };

  const handleDateClick = (date) => {
    if (!customStartDate || (customStartDate && customEndDate)) {
      setCustomStartDate(date);
      setCustomEndDate(null);
    } else if (customStartDate && !customEndDate) {
      if (date < customStartDate) {
        setCustomEndDate(customStartDate);
        setCustomStartDate(date);
      } else {
        setCustomEndDate(date);
      }
    }
  };

  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      setDateDisplay(`${formatDate(customStartDate)} - ${formatDate(customEndDate)}`);
      setShowCustomCalendar(false);
      setShowDateDropdown(false);
    }
  };

  const handleCancelCustomRange = () => {
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
    setCustomStartDate(null);
    setCustomEndDate(null);
  };

  const handleClearDate = (e) => {
    e.stopPropagation();
    setDatePreset('');
    setDateDisplay('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
  };

  const handleClearMaterialType = (e) => {
    e.stopPropagation();
    setSelectedMaterialType('all');
    setMaterialTypeInputValue('');
    setShowMaterialTypeDropdown(false);
    setCurrentPage(1);
  };

  const handleClearVehicleNo = (e) => {
    e.stopPropagation();
    setSelectedVehicleNo('all');
    setVehicleNoInputValue('');
    setShowVehicleNoDropdown(false);
    setCurrentPage(1);
  };

  const handleClearVehicleType = (e) => {
    e.stopPropagation();
    setSelectedVehicleType('all');
    setVehicleTypeInputValue('');
    setShowVehicleTypeDropdown(false);
    setCurrentPage(1);
  };

  const handleClearFromPlant = (e) => {
    e.stopPropagation();
    setSelectedFromPlant('all');
    setFromPlantInputValue('');
    setShowFromPlantDropdown(false);
    setCurrentPage(1);
  };

  const handleClearToSites = (e) => {
    e.stopPropagation();
    setSelectedToSites('all');
    setToSitesInputValue('');
    setShowToSitesDropdown(false);
    setCurrentPage(1);
  };

  const handleClearDCNumber = (e) => {
    e.stopPropagation();
    setSelectedDCNumber('all');
    setDCNumberInputValue('');
    setShowDCNumberDropdown(false);
    setCurrentPage(1);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  // Autocomplete handlers for Material Type
  const isTypingMaterialTypeRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingMaterialTypeRef.current) {
      if (selectedMaterialType === 'all') {
        setMaterialTypeInputValue('');
      } else {
        const selectedOption = materialTypeFilterOptions.find(opt => opt.value === selectedMaterialType);
        if (selectedOption) {
          setMaterialTypeInputValue(selectedOption.label);
        } else {
          setMaterialTypeInputValue('');
        }
      }
    }
  }, [selectedMaterialType, materialTypeFilterOptions]);

  const filteredMaterialTypeOptions = useMemo(() => {
    if (!materialTypeInputValue.trim()) {
      return materialTypeFilterOptions;
    }
    const searchTerm = materialTypeInputValue.toLowerCase();
    return materialTypeFilterOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [materialTypeFilterOptions, materialTypeInputValue]);

  const handleMaterialTypeInputChange = (e) => {
    const value = e.target.value;
    isTypingMaterialTypeRef.current = true;
    setMaterialTypeInputValue(value);
    setShowMaterialTypeDropdown(true);
    
    if (!value.trim()) {
      setSelectedMaterialType('all');
    }
    
    setTimeout(() => {
      isTypingMaterialTypeRef.current = false;
    }, 100);
  };

  const handleMaterialTypeSelect = (option) => {
    isTypingMaterialTypeRef.current = false;
    setSelectedMaterialType(option.value);
    setMaterialTypeInputValue(option.label);
    setShowMaterialTypeDropdown(false);
    setCurrentPage(1);
  };

  // Autocomplete handlers for Vehicle No
  const isTypingVehicleNoRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingVehicleNoRef.current) {
      if (selectedVehicleNo === 'all') {
        setVehicleNoInputValue('');
      } else {
        const selectedOption = vehicleNoOptions.find(opt => opt.value === selectedVehicleNo);
        if (selectedOption) {
          setVehicleNoInputValue(selectedOption.label);
        } else {
          setVehicleNoInputValue('');
        }
      }
    }
  }, [selectedVehicleNo, vehicleNoOptions]);

  const filteredVehicleNoOptions = useMemo(() => {
    if (!vehicleNoInputValue.trim()) {
      return vehicleNoOptions;
    }
    const searchTerm = vehicleNoInputValue.toLowerCase();
    return vehicleNoOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [vehicleNoOptions, vehicleNoInputValue]);

  const handleVehicleNoInputChange = (e) => {
    const value = e.target.value;
    isTypingVehicleNoRef.current = true;
    setVehicleNoInputValue(value);
    setShowVehicleNoDropdown(true);
    
    if (!value.trim()) {
      setSelectedVehicleNo('all');
    }
    
    setTimeout(() => {
      isTypingVehicleNoRef.current = false;
    }, 100);
  };

  const handleVehicleNoSelect = (option) => {
    isTypingVehicleNoRef.current = false;
    setSelectedVehicleNo(option.value);
    setVehicleNoInputValue(option.label);
    setShowVehicleNoDropdown(false);
    setCurrentPage(1);
  };

  // Autocomplete handlers for Vehicle Type
  const isTypingVehicleTypeRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingVehicleTypeRef.current) {
      if (selectedVehicleType === 'all') {
        setVehicleTypeInputValue('');
      } else {
        const selectedOption = vehicleTypeFilterOptions.find(opt => opt.value === selectedVehicleType);
        if (selectedOption) {
          setVehicleTypeInputValue(selectedOption.label);
        } else {
          setVehicleTypeInputValue('');
        }
      }
    }
  }, [selectedVehicleType, vehicleTypeFilterOptions]);

  const filteredVehicleTypeOptions = useMemo(() => {
    if (!vehicleTypeInputValue.trim()) {
      return vehicleTypeFilterOptions;
    }
    const searchTerm = vehicleTypeInputValue.toLowerCase();
    return vehicleTypeFilterOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [vehicleTypeFilterOptions, vehicleTypeInputValue]);

  const handleVehicleTypeInputChange = (e) => {
    const value = e.target.value;
    isTypingVehicleTypeRef.current = true;
    setVehicleTypeInputValue(value);
    setShowVehicleTypeDropdown(true);
    
    if (!value.trim()) {
      setSelectedVehicleType('all');
    }
    
    setTimeout(() => {
      isTypingVehicleTypeRef.current = false;
    }, 100);
  };

  const handleVehicleTypeSelect = (option) => {
    isTypingVehicleTypeRef.current = false;
    setSelectedVehicleType(option.value);
    setVehicleTypeInputValue(option.label);
    setShowVehicleTypeDropdown(false);
    setCurrentPage(1);
  };

  // Autocomplete handlers for From Plant
  const isTypingFromPlantRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingFromPlantRef.current) {
      if (selectedFromPlant === 'all') {
        setFromPlantInputValue('');
      } else {
        const selectedOption = fromPlantFilterOptions.find(opt => opt.value === selectedFromPlant);
        if (selectedOption) {
          setFromPlantInputValue(selectedOption.label);
        } else {
          setFromPlantInputValue('');
        }
      }
    }
  }, [selectedFromPlant, fromPlantFilterOptions]);

  const filteredFromPlantOptions = useMemo(() => {
    if (!fromPlantInputValue.trim()) {
      return fromPlantFilterOptions;
    }
    const searchTerm = fromPlantInputValue.toLowerCase();
    return fromPlantFilterOptions.filter(opt =>
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [fromPlantFilterOptions, fromPlantInputValue]);

  const handleFromPlantInputChange = (e) => {
    const value = e.target.value;
    isTypingFromPlantRef.current = true;
    setFromPlantInputValue(value);
    setShowFromPlantDropdown(true);
    
    if (!value.trim()) {
      setSelectedFromPlant('all');
    }
    
    setTimeout(() => {
      isTypingFromPlantRef.current = false;
    }, 100);
  };

  const handleFromPlantSelect = (option) => {
    isTypingFromPlantRef.current = false;
    setSelectedFromPlant(option.value);
    setFromPlantInputValue(option.label);
    setShowFromPlantDropdown(false);
    setCurrentPage(1);
  };

  // Autocomplete handlers for To Sites
  const isTypingToSitesRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingToSitesRef.current) {
      if (selectedToSites === 'all') {
        setToSitesInputValue('');
      } else {
        const selectedOption = toSitesFilterOptions.find(opt => opt.value === selectedToSites);
        if (selectedOption) {
          setToSitesInputValue(selectedOption.label);
        } else {
          setToSitesInputValue('');
        }
      }
    }
  }, [selectedToSites, toSitesFilterOptions]);

  const filteredToSitesOptions = useMemo(() => {
    if (!toSitesInputValue.trim()) {
      return toSitesFilterOptions;
    }
    const searchTerm = toSitesInputValue.toLowerCase();
    return toSitesFilterOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [toSitesFilterOptions, toSitesInputValue]);

  const handleToSitesInputChange = (e) => {
    const value = e.target.value;
    isTypingToSitesRef.current = true;
    setToSitesInputValue(value);
    setShowToSitesDropdown(true);
    
    if (!value.trim()) {
      setSelectedToSites('all');
    }
    
    setTimeout(() => {
      isTypingToSitesRef.current = false;
    }, 100);
  };

  const handleToSitesSelect = (option) => {
    isTypingToSitesRef.current = false;
    setSelectedToSites(option.value);
    setToSitesInputValue(option.label);
    setShowToSitesDropdown(false);
    setCurrentPage(1);
  };

  // Autocomplete handlers for DC Number
  const isTypingDCNumberRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingDCNumberRef.current) {
      if (selectedDCNumber === 'all') {
        setDCNumberInputValue('');
      } else {
        const selectedOption = dcNumberOptions.find(opt => opt.value === selectedDCNumber);
        if (selectedOption) {
          setDCNumberInputValue(selectedOption.label);
        } else {
          setDCNumberInputValue('');
        }
      }
    }
  }, [selectedDCNumber, dcNumberOptions]);

  const filteredDCNumberOptions = useMemo(() => {
    if (!dcNumberInputValue.trim()) {
      return dcNumberOptions;
    }
    const searchTerm = dcNumberInputValue.toLowerCase();
    return dcNumberOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [dcNumberOptions, dcNumberInputValue]);

  const handleDCNumberInputChange = (e) => {
    const value = e.target.value;
    isTypingDCNumberRef.current = true;
    setDCNumberInputValue(value);
    setShowDCNumberDropdown(true);
    
    if (!value.trim()) {
      setSelectedDCNumber('all');
    }
    
    setTimeout(() => {
      isTypingDCNumberRef.current = false;
    }, 100);
  };

  const handleDCNumberSelect = (option) => {
    isTypingDCNumberRef.current = false;
    setSelectedDCNumber(option.value);
    setDCNumberInputValue(option.label);
    setShowDCNumberDropdown(false);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setDatePreset('');
    setDateDisplay('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setSelectedMaterialType('all');
    setSelectedVehicleNo('all');
    setSelectedVehicleType('all');
    setSelectedFromPlant('all');
    setSelectedToSites('all');
    setSelectedDCNumber('all');
    setMaterialTypeInputValue('');
    setVehicleNoInputValue('');
    setVehicleTypeInputValue('');
    setFromPlantInputValue('');
    setToSitesInputValue('');
    setDCNumberInputValue('');
    setCurrentPage(1);
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
    setShowMaterialTypeDropdown(false);
    setShowVehicleNoDropdown(false);
    setShowVehicleTypeDropdown(false);
    setShowFromPlantDropdown(false);
    setShowToSitesDropdown(false);
    setShowDCNumberDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDateDropdown && !event.target.closest('.date-dropdown-container')) {
        setShowDateDropdown(false);
      }
      if (showMaterialTypeDropdown && !event.target.closest('.material-type-autocomplete-container')) {
        setShowMaterialTypeDropdown(false);
      }
      if (showVehicleNoDropdown && !event.target.closest('.vehicle-no-autocomplete-container')) {
        setShowVehicleNoDropdown(false);
      }
      if (showVehicleTypeDropdown && !event.target.closest('.vehicle-type-autocomplete-container')) {
        setShowVehicleTypeDropdown(false);
      }
      if (showFromPlantDropdown && !event.target.closest('.from-plant-autocomplete-container')) {
        setShowFromPlantDropdown(false);
      }
      if (showToSitesDropdown && !event.target.closest('.to-sites-autocomplete-container')) {
        setShowToSitesDropdown(false);
      }
      if (showDCNumberDropdown && !event.target.closest('.dc-number-autocomplete-container')) {
        setShowDCNumberDropdown(false);
      }
      if (showColumnMenu && !event.target.closest('.column-menu-container')) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDateDropdown, showMaterialTypeDropdown, showVehicleNoDropdown, showVehicleTypeDropdown, showFromPlantDropdown, showToSitesDropdown, showDCNumberDropdown, showColumnMenu]);

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(16);
      doc.text('Stock Out Report', 14, 15);
      
      // Add date range if available
      if (dateDisplay) {
        doc.setFontSize(10);
        doc.text(`Date Range: ${dateDisplay}`, 14, 22);
      }
      
      // Check if there's data to export
      if (!filteredData || filteredData.length === 0) {
        doc.setFontSize(12);
        doc.text('No data available to export', 14, 30);
        doc.save(`StockOut_Report_${new Date().toISOString().slice(0,10)}.pdf`);
        return;
      }
      
      // Prepare table data
      const tableData = filteredData.map((item, index) => [
        item.id || index + 1,
        item.date || '',
        item.dcNumber || '',
        item.transporter || '',
        item.materialtype || '',
        item.grade || '',
        item.grosswt || '',
        item.tarewt || '',
        item.netwt || '',
        item.qty || '',
        item.vehicleno || '',
        item.vehicletype || '',
        item.fromplant || '',
        item.tosites || '',
        item.invoiceNumber || '',
        item.gstNumber || '',
        item.gstPercentage != null && item.gstPercentage !== ''
          ? `${String(item.gstPercentage).replace(/%$/, '').trim()}%`
          : '',
        getStockOutRowWithGST(item) || '',
        item.advance || item.totalamount || '',
        item.balanceAmount ?? '0'
      ]);
      
      // Add table
      autoTable(doc, {
        startY: dateDisplay ? 28 : 22,
        head: [['ID', 'Date', 'DC Number', 'Transporter', 'Material Type', 'Grade', 'Gross Wt', 'Tare Wt', 'Net Wt', 'Qty', 'Vehicle No', 'Vehicle Type', 'From Plant', 'To Sites', 'Invoice Number', 'GST Number', 'GST(%)', 'With GST', 'Advance to Driver', 'Balance']],
        body: tableData,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [0, 150, 136], textColor: 255 },
        margin: { top: 20 }
      });
      
      // Save the PDF
      doc.save(`StockOut_Report_${new Date().toISOString().slice(0,10)}.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const handleExportExcel = () => {
    const headers = ['ID','Date','DC Number','Transporter','Material Type','Grade','Gross Wt','Tare Wt','Net Wt','Qty','Vehicle No','Vehicle Type','From Plant','To Sites','Invoice Number','GST Number','GST(%)','With GST','Advance to Driver','Balance'];
    const rows = filteredData.map((item, index) => [
      item.id || index + 1,
      item.date,
      item.dcNumber,
      item.transporter,
      item.materialtype,
      item.grade || '',
      item.grosswt || '',
      item.tarewt || '',
      item.netwt || '',
      item.qty,
      item.vehicleno,
      item.vehicletype,
      item.fromplant,
      item.tosites,
      item.invoiceNumber || '',
      item.gstNumber || '',
      item.gstPercentage != null && item.gstPercentage !== ''
        ? `${String(item.gstPercentage).replace(/%$/, '').trim()}%`
        : '',
      getStockOutRowWithGST(item) || '',
      item.advance || item.totalamount,
      item.balanceAmount ?? '0'
    ]);
    
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock Out Report');
    
    // Set column widths
    const columnWidths = [
      { wch: 8 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 12 },
      { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 },
      { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 8 }, { wch: 12 }, { wch: 14 }, { wch: 12 }
    ];
    worksheet['!cols'] = columnWidths;
    
    XLSX.writeFile(workbook, `StockOut_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const handleGenerateReport = () => {
    // Export filtered rows to CSV
    const headers = ['ID','Date','DC Number','Material Type','Grade','Gross Wt','Tare Wt','Net Wt','Qty','Vehicle No','Vehicle Type','Transporter','From Plant','To Sites','Invoice Number','GST Number','GST(%)','With GST','Advance to Driver','Balance'];
    const rows = filteredData.map(item => [
      item.id, item.date, item.dcNumber, item.materialtype, item.grade, item.grosswt, item.tarewt, item.netwt, item.qty, 
      item.vehicleno, item.vehicletype, item.transporter, item.fromplant, item.tosites,
      item.invoiceNumber || '', item.gstNumber || '',
      item.gstPercentage != null && item.gstPercentage !== ''
        ? `${String(item.gstPercentage).replace(/%$/, '').trim()}%`
        : '',
      getStockOutRowWithGST(item) || '',
      item.advance || item.totalamount,
      item.balanceAmount ?? '0'
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => (v == null ? '' : String(v).replace(/"/g,'""'))).map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `StockOut_Report_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If material type changes, reset grade, rate and weight fields
    if (name === 'materialtype') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        grade: '', // Reset grade when material type changes
        rate: '', // Reset rate when material type changes
        grosswt: '', // Reset weight fields
        tarewt: '',
        netwt: ''
      }));
    } else if (name === 'grosswt' || name === 'tarewt') {
      // Auto-calculate netwt when grosswt or tarewt changes
      setFormData(prev => {
        const gross = name === 'grosswt' ? parseFloat(value) || 0 : parseFloat(prev.grosswt) || 0;
        const tare = name === 'tarewt' ? parseFloat(value) || 0 : parseFloat(prev.tarewt) || 0;
        const net = gross - tare;
        return {
          ...prev,
          [name]: value,
          netwt: net > 0 ? net.toFixed(3) : ''
        };
      });
    } else if (name === 'totalamount' || name === 'gstPercentage') {
      setFormData(prev => applyStockOutGstAndBalance({ ...prev, [name]: value }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'gstNumber' ? formatIndianGstNumber(value) : value
      }));
    }
  };

  const handleNumberInput = (e) => {
    // Allow: backspace, delete, tab, escape, enter, decimal point
    if ([46, 8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow: home, end, left, right
      (e.keyCode >= 35 && e.keyCode <= 39)) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.materialtype) {
      alert('Please select a Material Type');
      return;
    }
    
    // Validate grade for RMC and BT
    if ((formData.materialtype === 'RMC' || formData.materialtype === 'BT') && !formData.grade) {
      alert('Please enter/select Grade');
      return;
    }
    
    // Validate weight fields for BT with BC or BM grade
    if (formData.materialtype === 'BT' && (formData.grade === 'BC' || formData.grade === 'BM')) {
      if (!formData.grosswt) {
        alert('Please enter Gross Weight');
        return;
      }
      if (!formData.tarewt) {
        alert('Please enter Tare Weight');
        return;
      }
      if (!formData.netwt) {
        alert('Please enter Net Weight');
        return;
      }
    }
    
    // Validate quantity only for non-BT material types
    // if (formData.materialtype !== 'BT' && !formData.qty) {
    //   alert('Please enter Quantity');
    //   return;
    // }
    
    if (!formData.vehicleno) {
      alert('Please enter Vehicle Number');
      return;
    }
    
    if (!formData.fromplant) {
      alert('Please enter From Plant');
      return;
    }
    
    if (!formData.sitecode) {
      alert('Please select a Site Code');
      return;
    }

    const gstNoRaw = String(formData.gstNumber ?? '').trim();
    if (gstNoRaw && !isValidIndianGstNumber(gstNoRaw)) {
      alert('Please enter a valid GST Number (15 characters, e.g. 33AAACH7409R1Z8)');
      return;
    }
    
    if (!formData.supervisor) {
      alert('Please select Supervisor');
      return;
    }
    
    // Validate totalamount - skip for RMC and BT
    // if (!formData.totalamount && formData.materialtype !== 'RMC' && formData.materialtype !== 'BT') {
    //   alert('Please enter Total Amount');
    //   return;
    // }
    
    // For new records, call API to create stock out
    if (!isEditMode) {
      try {
        // Format date to YYYY-MM-DD for API
        const apiDate = formData.date || new Date().toISOString().split('T')[0];
        
        // Generate stockOut ID (or use empty string if not provided)
        //const stockOutId = formData.stockOut;

        const stockOutId = `StockOut#@${Date.now()}`;
        
        const siteCodeValue =
          siteCodes.find((opt) => opt.value === formData.sitecode)?.label || formData.sitecode;

        const gstPctRaw = String(formData.gstPercentage ?? '').trim();
        let gstPercentagePayload = null;
        if (gstPctRaw !== '') {
          const n = parseFloat(gstPctRaw);
          gstPercentagePayload = Number.isFinite(n) ? n : null;
        }

        // Prepare request body matching API format
        const requestBody = {
          stockOut: stockOutId,
          date: apiDate,
          dcNumber: formData.dcNumber || '',
          materialType: formData.materialtype || '',
          grade: formData.grade || '',
          gross: formData.grosswt || '',
          netWeight: (formData.netwt && formData.netwt !== '-') ? formData.netwt : '',
          vehicleNumber: formData.vehicleno || '',
          vehicleType: formData.vehicletype || '',
          transporter: formData.transporter || '',
          supervisor: formData.supervisor || '',
          fromPlants: formData.fromplant || '',
          toSites: siteCodeValue || '',
          invoiceNumber: formData.invoiceNumber || '',
          gstNumber: formData.gstNumber || '',
          gstPercentage: gstPercentagePayload,
          advanceToDriver: parseFloat(formData.advance) || 0,
          totalAmount: parseFloat(formData.totalamount) || 0,
          balanceAmount: parseFloat(formData.balanceAmount) || 0,
          tarWeight: formData.tarewt || '',
          quanity: formData.qty || '',
          type:"stockOutPlant",
        };
        
        // Use CORS proxy for the API call
        const apiUrl = `${API_BASE_URL}/api/stockIn/create/stockout`;
        
        // For POST requests, try direct fetch first
        let response;
        try {
          response = await fetch(apiUrl, {
            method: 'POST',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          });
        } catch (corsError) {
          // If CORS fails, we'll need to use a backend proxy or handle differently
          console.error('CORS Error:', corsError);
          alert('CORS Error: Unable to connect to the API server. Please configure CORS on the backend or use a backend proxy.');
          return;
        }
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        }
        
        const responseData = await response.json();
        console.log('Stock Out created successfully:', responseData);
        
        // Refresh stock out data from API to update the grid table
        try {
          const stockOutApiUrl = `${API_BASE_URL}/api/stockIn/stockout`;
          const apiData = await fetchWithCorsFallback(stockOutApiUrl);
          const transformedData = transformApiData(apiData);
          setStockOutData(transformedData);
          // Reset to first page to show the new record
          setCurrentPage(1);
        } catch (refreshError) {
          console.error('Error refreshing data:', refreshError);
          // Still show success even if refresh fails
        }
        
        setSuccessMessage('Stock Out added successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        handleClose();
        return;
      } catch (error) {
        console.error('Error creating stock out:', error);
        alert(`Failed to create stock out: ${error.message}`);
        return;
      }
    }
    
    // For Edit mode, call API to update
    if (isEditMode) {
      try {
        const record = stockOutData.find(
          (item) => item.stockOutId === String(editingId)
        );
        if (!record || !(record.stockOutId || record.stockOut)) {
          throw new Error('Record ID not found for update');
        }

        const apiDate = formData.date || new Date().toISOString().split('T')[0];
        const siteCodeValue =
          siteCodes.find((opt) => opt.value === formData.sitecode)?.label || formData.sitecode;

        const gstPctRaw = String(formData.gstPercentage ?? '').trim();
        let gstPercentagePayload = null;
        if (gstPctRaw !== '') {
          const n = parseFloat(gstPctRaw);
          gstPercentagePayload = Number.isFinite(n) ? n : null;
        }
        const requestBody = {
          stockOut: record.stockOut || record.stockOutId,
          date: apiDate,
          dcNumber: formData.dcNumber || '',
          materialType: formData.materialtype || '',
          grade: formData.grade || '',
          gross: formData.grosswt || '',
          netWeight: (formData.netwt && formData.netwt !== '-') ? formData.netwt : '',
          vehicleNumber: formData.vehicleno || '',
          vehicleType: formData.vehicletype || '',
          transporter: formData.transporter || '',
          supervisor: formData.supervisor || '',
          fromPlants: formData.fromplant || '',
          toSites: siteCodeValue || '',
          invoiceNumber: formData.invoiceNumber || '',
          gstNumber: formData.gstNumber || '',
          gstPercentage: gstPercentagePayload,
          advanceToDriver: parseFloat(formData.advance) || 0,
          totalAmount: parseFloat(formData.totalamount) || 0,
          tarWeight: formData.tarewt || '',
          quanity: formData.qty || '',
          balanceAmount: parseFloat(formData.balanceAmount) || 0,
          type: "stockOutPlant"
        };

        const apiUrl = `${API_BASE_URL}/api/stockIn/stockout`;
        const response = await fetch(apiUrl, {
          method: 'PUT',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        }

        // Refresh list from API
        try {
          const stockOutApiUrl = `${API_BASE_URL}/api/stockIn/stockout`;
          const apiData = await fetchWithCorsFallback(stockOutApiUrl);
          setStockOutData(transformApiData(apiData));
          setCurrentPage(1);
        } catch (refreshError) {
          console.error('Error refreshing after update:', refreshError);
        }

        setSuccessMessage('Stock Out updated successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        handleClose();
        return;
      } catch (error) {
        console.error('Error updating stock out:', error);
        alert(`Failed to update stock out: ${error.message}`);
        return;
      }
    }

    handleClose();
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingId(null);
    setFormData({
      date: getTodayDate(),
      dcNumber: '',
      transporter: '',
      supervisor: '',
      materialtype: '',
      grade: '',
      rate: '',
      grosswt: '',
      tarewt: '',
      netwt: '',
      qty: '',
      sitecode: '',
      vehicleno: '',
      vehicletype: '',
      fromplant: defaultPlant,
      tosites: '',
      totalamount: '',
      invoiceNumber: '',
      gstNumber: '',
      gstPercentage: '',
      advance: '',
      balanceAmount: ''
    });
    // Don't clear success message here - let it auto-hide after 3 seconds
  };

  const handleEdit = (stockOutId) => {
    const record = stockOutData.find((item) => item.stockOutId === String(stockOutId));
    if (record) {
      const ts = record.tosites ? String(record.tosites).trim() : '';
      const siteMatch = ts
        ? siteCodes.find(
            (opt) =>
              opt.label.toLowerCase() === ts.toLowerCase() ||
              opt.value.toLowerCase() === ts.toLowerCase()
          )
        : null;

      setFormData({
        date: record.date ? formatDateForInput(record.date) : getTodayDate(),
        dcNumber: record.dcNumber || '',
        transporter: record.transporter || '',
        supervisor: record.supervisor || '',
        materialtype: record.materialtype || '',
        grade: record.grade || '',
        rate: record.rate || '',
        grosswt: record.grosswt || '',
        tarewt: record.tarewt || '',
        netwt: record.netwt || '',
        qty: record.qty || '',
        vehicleno: record.vehicleno || '',
        vehicletype: record.vehicletype || '',
        fromplant: record.fromplant || '',
        tosites: record.tosites || '',
        sitecode: siteMatch ? siteMatch.value : '',
        totalamount: record.totalamount || '',
        invoiceNumber: record.invoiceNumber || '',
        gstNumber: formatIndianGstNumber(record.gstNumber || ''),
        gstPercentage:
          record.gstPercentage != null && record.gstPercentage !== ''
            ? String(record.gstPercentage).replace(/%$/, '').trim()
            : '',
        advance: record.advance || '',
        balanceAmount:
          record.balanceAmount != null && record.balanceAmount !== ''
            ? String(record.balanceAmount)
            : '',
      });
      setEditingId(record.stockOutId);
      setIsEditMode(true);
      setIsModalOpen(true);
    }
  };

  const handleDelete = (stockOutId) => {
   
    if (!stockOutId) return;
    setRecordToDelete(stockOutId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (recordToDelete == null) return;
    
    try {
      const record = stockOutData.find(
        (item) => item.stockOutId === String(recordToDelete)
      );
      const deleteId = record?.stockOutId || record?.stockOut;
      if (!record || !deleteId) {
        throw new Error('Record ID not found');
      }

      const apiUrl = `${API_BASE_URL}/api/stockIn/stockOut/${encodeURIComponent(deleteId)}`;
      
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      await fetchStockOutData();

      setSuccessMessage('Stock Out record deleted successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setShowDeleteConfirm(false);
      setRecordToDelete(null);
    } catch (error) {
      console.error('Error deleting stock out record:', error);
      setError(error.message || 'Failed to delete stock out record');
      setSuccessMessage('Failed to delete stock out record');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      setShowDeleteConfirm(false);
      setRecordToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setRecordToDelete(null);
  };

  const handleCloseSnackbar = (event, reason) => {
    setSnackbarOpen(false);
    setSuccessMessage('');
  };

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setShowSuccessPopup(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  return (
    <>
      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        style={{position: 'absolute', bottom: '10px', left: '10px'}}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          sx={{ 
            width: '100%', 
            backgroundColor: snackbarSeverity === 'success' ? '#43a047' : '#dc2626', 
            color: '#fff', 
            fontWeight: '400',
            fontSize: '18px' 
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      {error && (
        <div className="bg-red-200 border border-red-400 text-red-700 px-4 py-3 rounded" style={{position: 'absolute', bottom: '10px', left: '10px', zIndex: '1000', minWidth: '200px'}}>
          {error}
        </div>
      )}
      
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-poppins">Stock Out</h1>
          
        </div>
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={fetchStockOutData}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            title="Refresh"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v6h6M20 20v-6h-6M20 8a8 8 0 00-14.828-3M4 16a8 8 0 0014.828 3" />
            </svg>
          
          </button>
          {/* Column Visibility Toggle */}
          <div className="relative column-menu-container">
            <button 
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              title="Toggle Columns"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            
            </button>
            {showColumnMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
                <div className="py-1">
                  <div className="px-3 py-2 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={handleResetColumns}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Reset
                    </button>
                  </div>
                  {columnDefinitions.map(col => (
                    <label key={col.key} className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={visibleColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                        disabled={visibleColumns[col.key] && getVisibleColumnCount() === 1}
                        className="mr-3"
                      />
                      <span className="text-sm text-gray-700">{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          {isAdministrator && (
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              title={showFilters ? "Hide Filters" : "Show Filters"}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
              </svg>
              <span>{showFilters ? "Hide Filters" : "Show Filters"}</span>
            </button>
          )}
          <button
            onClick={() => {
              setIsEditMode(false);
              // Set current date and default plant when opening add modal
              setFormData(prev => ({
                ...prev,
                date: getTodayDate(),
                fromplant: defaultPlant
              }));
              setIsModalOpen(true);
            }}
            className="btn-primary"
          >
            Add Stock Out
          </button>
        </div>
      </div>

      {isAdministrator && showFilters && (
            <div className="p-4 flex flex-wrap items-end gap-4 bg-white shadow rounded-lg" style={{marginTop: '10px'}}>
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">
                {datePreset == '' ? 'Choose Date' : 'Selected Date Range: '} 
                {datePreset && <span className="font-bold text-black text-xs uppercase">({datePreset == "today" ? "Today" : datePreset == "yesterday" ? "Yesterday" : datePreset == "last7" ? "Last 7 Days" : datePreset == "last30" ? "Last 30 Days" : datePreset == "thisMonth" ? "This Month" : datePreset == "lastMonth" ? "Last Month" : datePreset == "custom" ? "Custom Range" : datePreset})</span>}
              </label>
              <div className="relative date-dropdown-container">
                <div className="relative">
                  <input
                    type="text"
                    value={dateDisplay || 'Select Range'}
                    readOnly
                    onClick={() => setShowDateDropdown(!showDateDropdown)}
                    className="w-full px-3 py-2 pl-10 pr-8 border border-gray-300 rounded-md text-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Select Date Range"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  {dateDisplay && (
                    <button
                      type="button"
                      onClick={handleClearDate}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 rounded-r-md"
                      title="Clear date"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                {showDateDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg z-20">
                    <div className="py-1">
                      <button
                        onClick={() => handleDatePresetChange('today')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'today' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Today
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('yesterday')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'yesterday' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Yesterday
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('last7')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'last7' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Last 7 Days
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('last30')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'last30' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Last 30 Days
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('thisMonth')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'thisMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        This Month
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('lastMonth')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'lastMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Last Month
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('custom')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'custom' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Custom Range
                      </button>
                    </div>
                  </div>
                )}
                {/* Custom Calendar Modal */}
                {showCustomCalendar && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-30 p-4">
                    <div className="flex">
                      {/* Left side - Preset options */}
                      <div className="w-48 pr-4 border-r border-gray-200">
                        <div className="space-y-1">
                          <button
                            onClick={() => handleDatePresetChange('today')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'today' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Today
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('yesterday')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'yesterday' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Yesterday
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('last7')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'last7' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Last 7 Days
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('last30')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'last30' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Last 30 Days
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('thisMonth')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'thisMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            This Month
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('lastMonth')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'lastMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Last Month
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('custom')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'custom' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Custom Range
                          </button>
                        </div>
                      </div>
                      
                      {/* Right side - Calendar */}
                      <div className="ml-4">
                        {/* Calendar Navigation */}
                        <div className="flex items-center justify-between mb-4">
                          <button
                            onClick={() => navigateMonth(-1)}
                            className="p-1 hover:bg-gray-100 rounded"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          <h3 className="text-sm font-medium text-gray-900">
                            {currentMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                          </h3>
                          <button
                            onClick={() => navigateMonth(1)}
                            className="p-1 hover:bg-gray-100 rounded"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </div>
                        
                        {/* Calendar Grid */}
                        <div className="w-64">
                          {/* Days of week header */}
                          <div className="grid grid-cols-7 gap-1 mb-2">
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                              <div key={day} className="text-xs text-gray-500 text-center py-1">
                                {day}
                              </div>
                            ))}
                          </div>
                          
                          {/* Calendar days */}
                          <div className="grid grid-cols-7 gap-1">
                            {(() => {
                              const daysInMonth = getDaysInMonth(currentMonth);
                              const firstDay = getFirstDayOfMonth(currentMonth);
                              const days = [];
                              
                              // Previous month days
                              const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 0);
                              for (let i = firstDay - 1; i >= 0; i--) {
                                const day = prevMonth.getDate() - i;
                                days.push(
                                  <button
                                    key={`prev-${day}`}
                                    className="text-xs text-gray-400 py-1 hover:bg-gray-100 rounded"
                                    disabled
                                  >
                                    {day}
                                  </button>
                                );
                              }
                              
                              // Current month days
                              for (let day = 1; day <= daysInMonth; day++) {
                                const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                                const isSelected = isSameDate(date, customStartDate) || isSameDate(date, customEndDate);
                                const isInRange = customStartDate && customEndDate && isDateInRange(date, customStartDate, customEndDate);
                                
                                days.push(
                                  <button
                                    key={day}
                                    onClick={() => handleDateClick(date)}
                                    className={`text-xs py-1 rounded hover:bg-gray-100 ${
                                      isSelected 
                                        ? 'bg-orange-500 text-white' 
                                        : isInRange 
                                        ? 'bg-orange-100 text-orange-600' 
                                        : 'text-gray-700'
                                    }`}
                                  >
                                    {day}
                                  </button>
                                );
                              }
                              
                              // Next month days
                              const remainingCells = 42 - days.length; // 6 rows * 7 days
                              for (let day = 1; day <= remainingCells; day++) {
                                days.push(
                                  <button
                                    key={`next-${day}`}
                                    className="text-xs text-gray-400 py-1 hover:bg-gray-100 rounded"
                                    disabled
                                  >
                                    {day}
                                  </button>
                                );
                              }
                              
                              return days;
                            })()}
                          </div>
                        </div>
                        
                        {/* Selected date range display */}
                        <div className="mt-4 text-center">
                          <div className="text-sm text-gray-600 mb-3">
                            {customStartDate && customEndDate 
                              ? `${formatDate(customStartDate)} - ${formatDate(customEndDate)}`
                              : customStartDate 
                              ? `${formatDate(customStartDate)} - Select end date`
                              : 'Select start date'
                            }
                          </div>
                          
                          {/* Action buttons */}
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={handleCancelCustomRange}
                              className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleApplyCustomRange}
                              disabled={!customStartDate || !customEndDate}
                              className="px-4 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
                <div className="flex flex-col max-w-[130px]">
              <label className="text-xs text-gray-600 mb-1">Material Type</label>
              <div className="relative material-type-autocomplete-container">
                <input
                  type="text"
                  value={materialTypeInputValue}
                  onChange={handleMaterialTypeInputChange}
                  onFocus={() => {
                    setShowMaterialTypeDropdown(true);
                  }}
                  placeholder="Search "
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  {materialTypeInputValue ? (
                    <button
                      type="button"
                      onClick={handleClearMaterialType}
                      className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                      title="Clear material type"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <div className="pr-3 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  )}
                </div>
                {showMaterialTypeDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                    <div className="py-1">
                      {filteredMaterialTypeOptions.length > 0 ? (
                        filteredMaterialTypeOptions.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleMaterialTypeSelect(opt)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                              selectedMaterialType === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                          No matches found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col max-w-[120px]">
              <label className="text-xs text-gray-600 mb-1">Vehicle No</label>
              <div className="relative vehicle-no-autocomplete-container">
                <input
                  type="text"
                  value={vehicleNoInputValue}
                  onChange={handleVehicleNoInputChange}
                  placeholder="Enter Vehicle No"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  {vehicleNoInputValue ? (
                    <button
                      type="button"
                      onClick={handleClearVehicleNo}
                      className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                      title="Clear vehicle no"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <div className="pr-3 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col max-w-[120px]">
              <label className="text-xs text-gray-600 mb-1">Vehicle Type</label>
              <div className="relative vehicle-type-autocomplete-container">
                <input
                  type="text"
                  value={vehicleTypeInputValue}
                  onChange={handleVehicleTypeInputChange}
                  onFocus={() => {
                    setShowVehicleTypeDropdown(true);
                  }}
                  placeholder="Search"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  {vehicleTypeInputValue ? (
                    <button
                      type="button"
                      onClick={handleClearVehicleType}
                      className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                      title="Clear vehicle type"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <div className="pr-3 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  )}
                </div>
                {showVehicleTypeDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                    <div className="py-1">
                      {filteredVehicleTypeOptions.length > 0 ? (
                        filteredVehicleTypeOptions.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleVehicleTypeSelect(opt)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                              selectedVehicleType === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                          No matches found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col max-w-[120px]">
              <label className="text-xs text-gray-600 mb-1">From Plant</label>
              <div className="relative from-plant-autocomplete-container">
                <input
                  type="text"
                  value={fromPlantInputValue}
                  onChange={handleFromPlantInputChange}
                  onFocus={() => {
                    setShowFromPlantDropdown(true);
                  }}
                  readOnly={!isEditMode}
                  placeholder="Search"
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    !isEditMode ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''
                  }`}
                  autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  {fromPlantInputValue ? (
                    <button
                      type="button"
                      onClick={handleClearFromPlant}
                      className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                      title="Clear from plant"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <div className="pr-3 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  )}
                </div>
                {showFromPlantDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                    <div className="py-1">
                      {filteredFromPlantOptions.length > 0 ? (
                        filteredFromPlantOptions.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleFromPlantSelect(opt)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                              selectedFromPlant === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                          No matches found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
                <div className="flex flex-col max-w-[120px]">
              <label className="text-xs text-gray-600 mb-1">To Sites</label>
              <div className="relative to-sites-autocomplete-container">
                <input
                  type="text"
                  value={toSitesInputValue}
                  onChange={handleToSitesInputChange}
                  onFocus={() => {
                    setShowToSitesDropdown(true);
                  }}
                    placeholder="Search"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  {toSitesInputValue ? (
                    <button
                      type="button"
                      onClick={handleClearToSites}
                      className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                      title="Clear to sites"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <div className="pr-3 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  )}
                </div>
                {showToSitesDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                    <div className="py-1">
                      {filteredToSitesOptions.length > 0 ? (
                        filteredToSitesOptions.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleToSitesSelect(opt)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                              selectedToSites === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                          No matches found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col max-w-[120px]">
              <label className="text-xs text-gray-600 mb-1">DC (Delivery Challan)</label>
              <div className="relative dc-number-autocomplete-container">
                <input
                  type="text"
                  value={dcNumberInputValue}
                  onChange={handleDCNumberInputChange}
                  placeholder="Enter DC Number"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  {dcNumberInputValue ? (
                    <button
                      type="button"
                      onClick={handleClearDCNumber}
                      className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                      title="Clear DC number"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <div className="pr-3 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button 
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                title="Clear All Filters"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Clear All</span>
              </button>
              <button 
                onClick={handleExportPDF}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                title="Export as PDF"
              >
                <img src="/assets/img/pdf.png" alt="PDF" className="h-4 w-4" />
              </button>
              <button 
                onClick={handleExportExcel}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                title="Export as Excel"
              >
                <img src="/assets/img/sheet.png" alt="Excel" className="h-4 w-4" />
              </button>
            </div>
            </div>
          )}

      {isLoading ? (
        <div className="bg-white shadow rounded-lg p-8 flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="text-gray-600 text-sm">Loading data...</p>
          </div>
        </div>
      ) : (
        <Grid cols={1} className="p-0 flex flex-wrap items-end rounded-lg" style={{marginTop: '10px'}}>
          <div className="">
            <div className="overflow-x-auto">
              <div 
                className={`relative overflow-y-auto ${!showFilters ? 'bg-white' : ''}`}
                style={isAdministrator ? { height: 'calc(100vh - 370px)', backgroundColor: '#fff' } :  { height: 'calc(100vh - 270px)', backgroundColor: '#fff' }
                }
              >
                <table className="min-w-full divide-y divide-gray-200 shadow rounded-lg border border-gray-200">
                  <thead className="sticky top-0 z-10" style={{backgroundColor: "rgb(0, 150, 136)"}}>
                    <tr>
                      {visibleColumns.date && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('date')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Date</span>
                            {getSortIcon('date')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.dcNumber && (
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>DC Number</th>
                      )}
                      {visibleColumns.materialtype && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('materialtype')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Material Type</span>
                            {getSortIcon('materialtype')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.grade && (
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Grade(MM)</th>
                      )}
                      {visibleColumns.grosswt && (
                        <th
                          className="px-6 py-3 text-right text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{ backgroundColor: 'rgb(0, 150, 136)' }}
                          onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgb(0, 130, 120)')}
                          onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgb(0, 150, 136)')}
                          onClick={() => handleSort('grosswt')}
                        >
                          <div className="flex items-center justify-end space-x-1">
                            <span>Gross Wt</span>
                            {getSortIcon('grosswt')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.tarewt && (
                        <th 
                          className="px-6 py-3 text-right text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('tarewt')}
                        >
                          <div className="flex items-center justify-end space-x-1">
                            <span>Tare</span>
                            {getSortIcon('tarewt')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.netwt && (
                        <th 
                          className="px-6 py-3 text-right text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('netwt')}
                        >
                          <div className="flex items-center justify-end space-x-1">
                            <span>Net Wt</span>
                            {getSortIcon('netwt')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.qty && (
                        <th 
                          className="px-6 py-3 text-right text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('qty')}
                        >
                          <div className="flex items-center justify-end space-x-1">
                            <span>Quantity</span>
                            {getSortIcon('qty')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.vehicleno && (
                        <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Vehicle No</th>
                      )}
                      {visibleColumns.vehicletype && (
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Vehicle Type</th>
                      )}
                      {visibleColumns.transporter && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('transporter')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Transporter</span>
                            {getSortIcon('transporter')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.supervisor && (
                        <th
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('supervisor')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Supervisor</span>
                            {getSortIcon('supervisor')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.fromplant && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('fromplant')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>From Plant</span>
                            {getSortIcon('fromplant')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.tosites && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('tosites')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>To Sites</span>
                            {getSortIcon('tosites')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.invoiceNumber && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('invoiceNumber')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Invoice Number</span>
                            {getSortIcon('invoiceNumber')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.gstNumber && (
                        <th
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{ backgroundColor: 'rgb(0, 150, 136)' }}
                          onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgb(0, 130, 120)')}
                          onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgb(0, 150, 136)')}
                          onClick={() => handleSort('gstNumber')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>GST Number</span>
                            {getSortIcon('gstNumber')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.gstPercentage && (
                        <th 
                          className="px-6 py-3 text-right text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('gstPercentage')}
                        >
                          <div className="flex items-center justify-end space-x-1">
                            <span>GST(%)</span>
                            {getSortIcon('gstPercentage')}
                          </div>
                        </th>
                      )}
                      {/* {visibleColumns.withGST && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('withGST')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>With GST</span>
                            {getSortIcon('withGST')}
                          </div>
                        </th>
                      )} */}
                      {visibleColumns.totalamount && (
                        <th 
                          className="px-6 py-3 text-right text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('totalamount')}
                        >
                          <div className="flex items-center justify-end space-x-1">
                            <span>Advance to Driver</span>
                            {getSortIcon('totalamount')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.balanceAmount && (
                        <th 
                          className="px-6 py-3 text-right text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('balanceAmount')}
                        >
                          <div className="flex items-center justify-end space-x-1">
                            <span>Balance</span>
                            {getSortIcon('balanceAmount')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.actions && (
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {!isLoading && paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan={getVisibleColumnCount()} className="px-6 py-8 text-center text-sm text-gray-500">
                          No stock out records found.
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((record) => (
                        <tr key={record.stockOutId || record.id} className="hover:bg-gray-50">
                          {visibleColumns.date && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.date}</td>
                          )}
                          {visibleColumns.dcNumber && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.dcNumber}</td>
                          )}
                          {visibleColumns.materialtype && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.materialtype}</td>
                          )}
                          {visibleColumns.grade && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{record.grade}</td>
                          )}
                          {visibleColumns.grosswt && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.grosswt}</td>
                          )}
                          {visibleColumns.tarewt && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.tarewt}</td>
                          )}
                          {visibleColumns.netwt && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.netwt}</td>
                          )}
                          {visibleColumns.qty && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.qty}</td>
                          )}
                          {visibleColumns.vehicleno && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.vehicleno}</td>
                          )}
                          {visibleColumns.vehicletype && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{record.vehicletype}</td>
                          )}
                          {visibleColumns.transporter && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{record.transporter}</td>
                          )}
                          {visibleColumns.supervisor && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{record.supervisor}</td>
                          )}
                          {visibleColumns.fromplant && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{record.fromplant}</td>
                          )}
                          {visibleColumns.tosites && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{record.tosites}</td>
                          )}
                          {visibleColumns.invoiceNumber && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{record.invoiceNumber || '-'}</td>
                          )}
                          {visibleColumns.gstNumber && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">{record.gstNumber || '-'}</td>
                          )}
                          {visibleColumns.gstPercentage && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                              {record.gstPercentage != null && record.gstPercentage !== ''
                                ? `${String(record.gstPercentage).replace(/%$/, '').trim()}%`
                                : '-'}
                            </td>
                          )}
                          {/* {visibleColumns.withGST && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {(() => {
                                const w = getStockOutRowWithGST(record);
                                return w ? `₹${w}` : '-';
                              })()}
                            </td>
                          )} */}
                          {visibleColumns.totalamount && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">₹{record.advance || record.totalamount || '0'}</td>
                          )}
                          {visibleColumns.balanceAmount && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{record.balanceAmount ?? '0'}</td>
                          )}
                          {visibleColumns.actions && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleEdit(record.stockOutId)}
                                  className="text-indigo-600 hover:text-indigo-900 focus:outline-none focus:underline"
                                  title="Edit"
                                >
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDelete(record.stockOutId)}
                                  className="text-red-600 hover:text-red-900 focus:outline-none focus:underline"
                                  title="Delete"
                                >
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              totalItems={filteredData.length}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </Grid>
      )}

      {/* Add/Edit Stock Out Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 m-0 overflow-hidden" style={{marginTop: 0}}>
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleClose}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left shadow-xl transform transition-all modal-card app-modal-panel">
              <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col">
                {/* Header */}
                <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {isEditMode ? 'Edit Stock Out' : 'Add Stock Out'}
                  </h3>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Form content */}
                <div className="min-h-0 flex-1 overflow-y-auto bg-white px-6 py-4">
                  {/* 1. Location & Date */}
                  <ModalSection>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="date"
                          value={formData.date}
                          onChange={handleInputChange}
                          max={getTodayDate()}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          From Plant <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="fromplant"
                          value={formData.fromplant || defaultPlant}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          {fromPlantOptionsList.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Site Code <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="sitecode"
                          value={formData.sitecode}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">Select Site Code</option>
                          {siteCodes.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </ModalSection>

                  {/* 2. Reference Details */}
                  <ModalSection>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          DC (Delivery Challan) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="dcNumber"
                          value={formData.dcNumber}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder=""
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Invoice No.
                        </label>
                        <input
                          type="text"
                          name="invoiceNumber"
                          value={formData.invoiceNumber || ''}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          GST Number
                        </label>
                        <input
                          type="text"
                          name="gstNumber"
                          value={formData.gstNumber || ''}
                          onChange={handleInputChange}
                          maxLength={15}
                          placeholder=""
                          autoComplete="off"
                          spellCheck={false}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono uppercase"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          GST (%) 
                        </label>
                        <input
                          type="text"
                          name="gstPercentage"
                          value={formData.gstPercentage || ''}
                          onChange={handleInputChange}
                          onKeyDown={handleNumberInput}
                          placeholder=""
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    </div>
                  </ModalSection>

                  {/* 3. Material Details */}
                  <ModalSection>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
                      <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Material Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="materialtype"
                        value={formData.materialtype}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select</option>
                        {materialTypeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Grade - Show when selected material has grades */}
                    {(() => {
                      const _mat = stockOutMaterialsFromApi.find(
                        (m) => String(m.materialType ?? m.materialtype ?? '').toLowerCase() === String(formData.materialtype).toLowerCase()
                      );
                      const _rawGrades = Array.isArray(_mat?.grades)
                        ? _mat.grades
                        : (Array.isArray(_mat?.materialGrade)
                          ? _mat.materialGrade
                          : (Array.isArray(_mat?.materialgrade) ? _mat.materialgrade : []));
                      const _apiGrades = _rawGrades
                        .map((g) => (typeof g === 'string' ? g : (g?.grade ?? g?.name ?? '')))
                        .map((x) => String(x).trim())
                        .filter(Boolean);
                      const _lc = String(formData.materialtype ?? '').toLowerCase();
                      const _showHardcoded = _lc === 'rmc' || _lc === 'bt';
                      if (_apiGrades.length === 0 && !_showHardcoded) return null;
                      return (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Grade <span className="text-red-500">*</span>
                          </label>
                          {_apiGrades.length > 0 ? (
                            <select
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Grade</option>
                              {_apiGrades.map((g) => (
                                <option key={g} value={g}>{g}</option>
                              ))}
                            </select>
                          ) : formData.materialtype === 'RMC' ? (
                            <select
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Grade</option>
                              <option value="M10">M10</option>
                              <option value="M15">M15</option>
                              <option value="M20">M20</option>
                              <option value="M25">M25</option>
                              <option value="M30">M30</option>
                              <option value="M35">M35</option>
                              <option value="M40">M40</option>
                            </select>
                          ) : (
                            <select
                              name="grade"
                              value={formData.grade}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Grade</option>
                              <option value="BC">BC</option>
                              <option value="BM">BM</option>
                            </select>
                          )}
                        </div>
                      );
                    })()}

                    {/* Quantity (M3cube) - Show for RMC, next to Grade */}
                    {formData.materialtype === 'RMC' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Quantity (M3cube)
                        </label>
                        <input
                          type=""
                          name="qty"
                          value={formData.qty}
                          onChange={handleInputChange}
                          onKeyDown={handleNumberInput}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    )}
                    </div>
                  </ModalSection>

                  {/* 4. Quantity & Amount */}
                  <ModalSection>
                    {formData.materialtype === 'BT' && (formData.grade === 'BC' || formData.grade === 'BM') && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
                        <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Gross Weight <span className="text-red-500">*</span>
                        </label>
                        <input
                          type=""
                          name="grosswt"
                          value={formData.grosswt}
                          onChange={handleInputChange}
                          onKeyDown={handleNumberInput}
                          step="0.001"
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tare Weight <span className="text-red-500">*</span>
                          </label>
                        <input
                          type=""
                          name="tarewt"
                          value={formData.tarewt}
                          onChange={handleInputChange}
                          onKeyDown={handleNumberInput}
                          step="0.001"
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Net Weight
                          </label>
                        <input
                          type=""
                          name="netwt"
                          value={formData.netwt}
                          onChange={handleInputChange}
                          onKeyDown={handleNumberInput}
                          step="0.001"
                          required
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-100 text-gray-600 cursor-not-allowed"
                        />
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {/* {formData.materialtype !== 'RMC' && formData.materialtype !== 'BT' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Amount <span className="text-red-500">*</span>
                          </label>
                          <input
                            type=""
                            name="totalamount"
                            value={formData.totalamount}
                            onChange={handleInputChange}
                            onKeyDown={handleNumberInput}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                      )} */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Advance to Driver
                        </label>
                        <input
                          type=""
                          name="advance"
                          value={formData.advance}
                          onChange={handleInputChange}
                          onKeyDown={handleNumberInput}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      {/* <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Balance (₹)
                        </label>
                        <input
                          type="text"
                          name="balanceAmount"
                          value={formData.balanceAmount || ''}
                          onChange={handleInputChange}
                          onKeyDown={handleNumberInput}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-100 text-gray-600 cursor-not-allowed"
                        />
                      </div> */}
                    </div>

                    {/* <div className="mt-2 rounded-md bg-green-50 px-4 py-3 text-sm text-teal-800">
                      <span className="mr-1">ⓘ</span>
                      Balance is calculated as: Total Amount + GST Amount – Advance
                    </div> */}
                  </ModalSection>

                  {/* 5. Additional Details */}
                  <ModalSection>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Transporter Name <span className="text-red-500">*</span>
                        </label>
                      <input
                        type="text"
                        name="transporter"
                        value={formData.transporter}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Vehicle Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="vehicleno"
                          value={formData.vehicleno}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Vehicle Type
                        </label>
                        <select
                          name="vehicletype"
                          value={formData.vehicletype}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">Select</option>
                          <option value="Own">TKD</option>
                          <option value="Rent">Rent</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Supervisor <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="supervisor"
                          value={formData.supervisor}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">Select Supervisor</option>
                          {supervisorsFromApi.length > 0
                            ? supervisorsFromApi.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))
                            : supervisorOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                        </select>
                      </div>
                    </div>
                  </ModalSection>
                </div>

                {/* Footer */}
                <div className="flex flex-shrink-0 justify-end space-x-3 border-t border-gray-200 bg-gray-50 px-6 py-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    style={{ backgroundColor: '#009688' }}
                  >
                    {isEditMode ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Background overlay */}
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={handleCancelDelete}
          ></div>

          {/* Confirmation Dialog */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all">
            <div className="p-6">
              {/* Warning Icon */}
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              
              {/* Message */}
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                Confirm Delete
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Are you sure you want to delete this record{recordToDelete ? ` (${recordToDelete})` : ''}? This action cannot be undone.
              </p>
              
              {/* Action Buttons */}
              <div className="flex gap-2 justify-center">
                <button
                  type="button"
                  onClick={handleCancelDelete}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="px-6 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 hover:opacity-90"
                  style={{ backgroundColor: '#dc2626' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default StockOut;

